/**
 * LoginPage — `/login` (v0.14 PR-9 wizard rewrite).
 *
 * One screen handles five sign-in/sign-up flows behind a 3-step wizard:
 *   Step 1  — role: child vs parent
 *   Step 2  — mode: login vs register (parent also gets forgot-password)
 *   Step 3  — the actual form, with child/parent registration multi-substeps
 *             for "enter verification code" and the parent password-reset
 *             "set new password" step.
 *
 * Three deep-link patterns short-circuit straight to step 3:
 *   1. `?invite=CODE`                              → parent / register / form
 *   2. `?parent_activation_email&code`             → parent / register / code
 *   3. `?parent_reset_email&code`                  → parent / forgot   / set
 * Invite codes also persist in the `xpb_invite` cookie for 30 days so a
 * stale share link still routes the user toward parent-register today.
 *
 * 调用关系：
 *   - api.login / register* / parent* / passwordReset* → backend/src/routes/auth.js
 *   - setSession + clearInviteCookie persist auth and clear attribution
 *   - PARENT_HOME redirect after a parent login goes to the admin SPA
 *
 * 关键约束：
 *   - All seven backend API signatures must stay identical — this file
 *     only changes the UI flow
 *   - iPad detection (isIpad) is heuristic; iPadOS 13+ identifies as Mac
 *     and is distinguished by maxTouchPoints
 *   - Cookies require explicit secure flag when window.location is https
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { api, ApiCallError, setSession } from '../api/client';
import { t, useLang } from '../i18n';
import PwaInstallHint from '../components/PwaInstallHint';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

// ─── Wizard model ────────────────────────────────────────────────────────
// PR-9 — replaced the old tabbed monolith with an explicit 3-step wizard:
//   Step 1 — Who are you?    ('child' | 'parent')
//   Step 2 — Have an account?('login' | 'register')
//   Step 3 — The actual form (login/register/forgot; multi-substep for
//                             code-verify + password-set)
//
// All seven backend API methods are unchanged. Invite cookie capture,
// parent-activation deep links, and parent password-reset deep links all
// still drop the user straight into the matching step 3 screen so they
// never have to think about which path they're on.

type Role = 'child' | 'parent';
type Mode = 'login' | 'register' | 'forgot';
type ChildRegSub = 'form' | 'code';
type ParentRegSub = 'form' | 'code';
type ForgotSub = 'email' | 'set';

const PARENT_HOME = '/parent/';
const PARENT_INVITE_NUDGE_KEY = 'xpb.parentInviteNudge';
const TOTAL_STEPS = 3;

function isIpad(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad/.test(ua)) return true;
  // iPadOS 13+ identifies as Mac; distinguish via touch points.
  if (/Mac/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

// ─── Invite-code cookie ────────────────────────────────────────────────────
// When a friend opens our share link (`?invite=…`) we stash the code in a
// cookie so it survives:
//   • bouncing in and out of the page before they hit "register"
//   • opening the link, getting distracted, coming back days later
//   • iOS Safari "Add to Home Screen" launching a fresh URL without the
//     query param
// Using a real cookie keeps the door open to do reward attribution
// server-side later if we want.
const INVITE_COOKIE = 'xpb_invite';
const INVITE_COOKIE_MAX_AGE_S = 30 * 24 * 60 * 60; // 30 days

function readInviteCookie(): string {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(/(?:^|;\s*)xpb_invite=([^;]+)/);
  if (!m) return '';
  try { return decodeURIComponent(m[1]).toLowerCase(); }
  catch { return ''; }
}

function writeInviteCookie(code: string): void {
  if (typeof document === 'undefined' || !code) return;
  const parts = [
    `${INVITE_COOKIE}=${encodeURIComponent(code)}`,
    'path=/', `max-age=${INVITE_COOKIE_MAX_AGE_S}`, 'samesite=lax',
  ];
  if (window.location.protocol === 'https:') parts.push('secure');
  document.cookie = parts.join('; ');
}

function clearInviteCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${INVITE_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

export default function LoginPage({ onLogin }: { onLogin: () => Promise<void> }) {
  useLang();

  // Wizard navigation
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [role, setRole] = useState<Role | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);

  // Child login
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  // Child register
  const [childRegSub, setChildRegSub] = useState<ChildRegSub>('form');
  const [cName, setCName] = useState('');
  const [cPwd, setCPwd] = useState('');
  const [cParentEmail, setCParentEmail] = useState('');
  const [cParentOpen, setCParentOpen] = useState(false);
  const [cCode, setCCode] = useState('');
  const [cDevCode, setCDevCode] = useState<string | null>(null);

  // Parent login
  const [pEmail, setPEmail] = useState('');
  const [pPassword, setPPassword] = useState('');

  // Parent register
  const [parentRegSub, setParentRegSub] = useState<ParentRegSub>('form');
  const [rEmail, setREmail] = useState('');
  const [rPassword, setRPassword] = useState('');
  const [rInviteCode, setRInviteCode] = useState('');
  const [rCode, setRCode] = useState('');
  const [rDevCode, setRDevCode] = useState<string | null>(null);

  // Parent password reset
  const [forgotSub, setForgotSub] = useState<ForgotSub>('email');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetDevCode, setResetDevCode] = useState<string | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ─── URL + cookie pickup ──────────────────────────────────────────────
  // Three deep-link patterns drop the user straight into step 3:
  //   1. ?invite=CODE                    → parent / register / form
  //   2. ?parent_activation_email&code   → parent / register / code
  //   3. ?parent_reset_email&code        → parent / forgot   / set
  // The xpb_invite cookie is also checked (same as #1) so a share-link
  // visit days ago still nudges the user toward parent-register today.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);

    const fromUrl = (params.get('invite') || '').trim().toLowerCase();
    const fromCookie = readInviteCookie();
    const code = fromUrl || fromCookie;
    if (fromUrl && fromUrl !== fromCookie) writeInviteCookie(fromUrl);
    if (code) {
      setRInviteCode(code);
      jump(3, 'parent', 'register');
    }

    // ?as=parent hints "I'm a parent" but doesn't lock in login/register —
    // drop them at step 2 (login vs register?) instead of step 1.
    if (params.get('as') === 'parent') jump(2, 'parent', null);

    const actEmail = (params.get('parent_activation_email') || '').trim().toLowerCase();
    const actCode = (params.get('parent_activation_code') || '').replace(/\D/g, '').slice(0, 6);
    if (actEmail && actCode) {
      setREmail(actEmail); setRCode(actCode); setParentRegSub('code');
      jump(3, 'parent', 'register');
    }

    const rstEmail = (params.get('parent_reset_email') || '').trim().toLowerCase();
    const rstCode = (params.get('parent_reset_code') || '').replace(/\D/g, '').slice(0, 6);
    if (rstEmail && rstCode) {
      setResetEmail(rstEmail); setResetCode(rstCode); setForgotSub('set');
      jump(3, 'parent', 'forgot');
    }
  }, []);

  function clearStatus() { setErr(null); }

  function jump(s: 1 | 2 | 3, r?: Role | null, m?: Mode | null) {
    setStep(s);
    if (r !== undefined) setRole(r);
    if (m !== undefined) setMode(m);
    setErr(null);
  }

  // Back button — pops within step-3 substeps first, then out to step 2/1.
  function back() {
    if (step === 3) {
      if (role === 'child' && mode === 'register' && childRegSub === 'code') {
        setChildRegSub('form'); clearStatus(); return;
      }
      if (role === 'parent' && mode === 'register' && parentRegSub === 'code') {
        setParentRegSub('form'); clearStatus(); return;
      }
      if (role === 'parent' && mode === 'forgot' && forgotSub === 'set') {
        setForgotSub('email'); clearStatus(); return;
      }
      jump(2); return;
    }
    if (step === 2) jump(1, null, null);
  }

  // ─── API call wrappers (signatures unchanged) ──────────────────────────
  async function submitChildLogin() {
    clearStatus();
    if (!name.trim() || password.length < 4) { setErr(t('auth.error.childMissing')); return; }
    setLoading(true);
    try {
      const r = await api.login(name.trim(), password);
      setSession(r.token, 'child');
      await onLogin();
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function childRegisterStart() {
    clearStatus();
    if (!cName.trim() || cPwd.length < 4) { setErr(t('auth.error.childMissing')); return; }
    const parentEmail = cParentEmail.trim().toLowerCase();
    if (cParentOpen && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail)) { setErr(t('auth.error.emailFormat')); return; }
    setLoading(true);
    try {
      const r = await api.registerStart(cName.trim(), cPwd, cParentOpen ? parentEmail : undefined);
      if (r.token) {
        setSession(r.token, 'child');
        if (r.standalone) window.localStorage.setItem(PARENT_INVITE_NUDGE_KEY, '1');
        await onLogin();
        return;
      }
      if (r.devCode) setCDevCode(r.devCode);
      setChildRegSub('code');
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function childRegisterComplete() {
    clearStatus();
    if (!/^\d{6}$/.test(cCode)) { setErr(t('auth.error.codeFormat')); return; }
    setLoading(true);
    try {
      const r = await api.registerComplete(cName.trim(), cParentEmail.trim().toLowerCase(), cCode);
      setSession(r.token, 'child');
      await onLogin();
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function parentLoginPassword() {
    clearStatus();
    if (!pEmail.trim()) { setErr(t('auth.error.emailMissing')); return; }
    if (!pPassword) { setErr(t('auth.error.passwordMissing')); return; }
    setLoading(true);
    try {
      const r = await api.parentLoginWithPassword(pEmail.trim().toLowerCase(), pPassword);
      setSession(r.token, 'parent');
      window.location.href = PARENT_HOME;
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function parentRegisterStart() {
    clearStatus();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rEmail.trim())) { setErr(t('auth.error.emailFormat')); return; }
    if (rPassword.length < 6) { setErr(t('auth.error.passwordShort')); return; }
    setLoading(true);
    try {
      const code = rInviteCode.trim().toLowerCase() || undefined;
      const r = await api.parentRegisterStart(rEmail.trim().toLowerCase(), rPassword, code);
      if (r.devCode) setRDevCode(r.devCode);
      setParentRegSub('code');
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function parentRegisterComplete() {
    clearStatus();
    if (!/^\d{6}$/.test(rCode)) { setErr(t('auth.error.codeFormat')); return; }
    setLoading(true);
    try {
      const r = await api.parentRegisterComplete(rEmail.trim().toLowerCase(), rCode);
      setSession(r.token, 'parent');
      // Reward attribution is locked in on the server side now — we no
      // longer need the invite cookie to follow this device around.
      clearInviteCookie();
      window.location.href = PARENT_HOME;
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function parentResetStart() {
    clearStatus();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail.trim())) { setErr(t('auth.error.emailFormat')); return; }
    setLoading(true);
    try {
      const r = await api.parentPasswordResetStart(resetEmail.trim().toLowerCase());
      if (r.devCode) setResetDevCode(r.devCode);
      setForgotSub('set');
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function parentResetComplete() {
    clearStatus();
    if (!/^\d{6}$/.test(resetCode)) { setErr(t('auth.error.codeFormat')); return; }
    if (resetPassword.length < 6) { setErr(t('auth.error.passwordShort')); return; }
    setLoading(true);
    try {
      await api.parentPasswordResetComplete(resetEmail.trim().toLowerCase(), resetCode, resetPassword);
      // After reset, flip back to the parent-login screen with the email
      // prefilled so they can try out their new password immediately.
      setPEmail(resetEmail.trim().toLowerCase());
      setPPassword('');
      setResetPassword(''); setResetCode(''); setForgotSub('email');
      jump(3, 'parent', 'login');
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  const step3Title = useMemo(() => {
    if (role === 'child' && mode === 'login') return t('auth.wizard.step3.title.childLogin');
    if (role === 'child' && mode === 'register') return t('auth.wizard.step3.title.childRegister');
    if (role === 'parent' && mode === 'login') return t('auth.wizard.step3.title.parentLogin');
    if (role === 'parent' && mode === 'register') return t('auth.wizard.step3.title.parentRegister');
    if (role === 'parent' && mode === 'forgot') return t('auth.wizard.step3.title.parentForgot');
    return '';
  }, [role, mode]);

  return (
    <div className="page" style={{ maxWidth: 440, padding: '32px 16px 88px' }}>
      <BrandHeader />

      {/* Step indicator + back affordance — hidden on step 1. */}
      {step >= 2 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <button type="button" onClick={back} disabled={loading} style={BACK_BTN}>
            {t('auth.wizard.back')}
          </button>
          <StepDots step={step} total={TOTAL_STEPS} />
        </div>
      )}

      <Card>
        {step === 1 && (
          <div>
            <BigHeading>{t('auth.wizard.role.title')}</BigHeading>
            <Sub>{t('auth.wizard.role.subtitle')}</Sub>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <BigChoice emoji="🧒" title={t('auth.wizard.role.child.title')}
                         subtitle={t('auth.wizard.role.child.sub')}
                         onClick={() => jump(2, 'child', null)} />
              <BigChoice emoji="👨‍👩‍👧" title={t('auth.wizard.role.parent.title')}
                         subtitle={t('auth.wizard.role.parent.sub')}
                         onClick={() => jump(2, 'parent', null)} />
            </div>
          </div>
        )}

        {step === 2 && role && (
          <div>
            <BigHeading>{t(role === 'child' ? 'auth.wizard.has.title.child' : 'auth.wizard.has.title.parent')}</BigHeading>
            <Sub>{t(role === 'child' ? 'auth.wizard.has.subtitle.child' : 'auth.wizard.has.subtitle.parent')}</Sub>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <BigChoice emoji="🔑" title={t('auth.wizard.has.login.title')}
                         subtitle={t('auth.wizard.has.login.sub')}
                         onClick={() => jump(3, undefined, 'login')} />
              <BigChoice emoji="✨" title={t('auth.wizard.has.register.title')}
                         subtitle={t(role === 'child' ? 'auth.wizard.has.register.sub.child' : 'auth.wizard.has.register.sub.parent')}
                         onClick={() => jump(3, undefined, 'register')} />
            </div>
          </div>
        )}

        {step === 3 && role && mode && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginTop: 0, marginBottom: 14, color: 'var(--xpb-text-primary)' }}>
              {step3Title}
            </h2>

            {/* ─── Child login ─── */}
            {role === 'child' && mode === 'login' && (
              <>
                <BigLabel>{t('auth.child.name.label')}</BigLabel>
                <input value={name} onChange={e => setName(e.target.value)} maxLength={20}
                       autoComplete="username" placeholder={t('auth.child.name.placeholder')} style={BIG_INPUT} />
                <BigLabel topGap>{t('auth.child.password.label')}</BigLabel>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && submitChildLogin()}
                       autoComplete="current-password" style={BIG_INPUT} />
                <ErrorBox err={err} />
                <Button block size="lg" style={BIG_CTA} disabled={loading} loading={loading} onClick={submitChildLogin}>
                  {t('auth.child.submit')}
                </Button>
                <div className="muted" style={{ textAlign: 'center', marginTop: 16, fontSize: 13, lineHeight: 1.7 }}>
                  {t('auth.child.help.line1')}<br />{t('auth.child.help.line2')}
                </div>
              </>
            )}

            {/* ─── Child register ─── */}
            {role === 'child' && mode === 'register' && childRegSub === 'form' && (
              <>
                <div className="muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
                  {t('auth.child.register.intro')}
                </div>
                <BigLabel>{t('auth.child.name.label')}</BigLabel>
                <input value={cName} onChange={e => setCName(e.target.value)} maxLength={20}
                       autoComplete="username" placeholder={t('auth.child.name.placeholder')} style={BIG_INPUT} />
                <BigLabel topGap>{t('auth.child.password.label')}</BigLabel>
                <input type="password" value={cPwd} onChange={e => setCPwd(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && childRegisterStart()}
                       autoComplete="new-password" style={BIG_INPUT} />
                <button type="button"
                        onClick={() => { setCParentOpen(!cParentOpen); setCCode(''); setCDevCode(null); clearStatus(); }}
                        style={{
                          width: '100%', marginTop: 16, padding: '12px 14px', borderRadius: 12,
                          border: '1px solid rgba(245,158,11,.35)',
                          background: cParentOpen ? 'rgba(245,158,11,.12)' : 'white',
                          color: 'var(--xpb-text-primary)', fontWeight: 800, textAlign: 'left', cursor: 'pointer',
                        }}>
                  {cParentOpen ? t('auth.child.register.parentToggle.open') : t('auth.child.register.parentToggle.closed')}
                </button>
                {cParentOpen && (
                  <>
                    <BigLabel topGap>{t('auth.child.register.parentEmail.label')}</BigLabel>
                    <input type="email" value={cParentEmail} onChange={e => setCParentEmail(e.target.value)}
                           onKeyDown={e => e.key === 'Enter' && childRegisterStart()}
                           autoComplete="email" placeholder={t('auth.parent.email.placeholder')} style={BIG_INPUT} />
                  </>
                )}
                <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                  {t('auth.child.register.rewardHint')}
                </div>
                <ErrorBox err={err} />
                <Button block size="lg" style={BIG_CTA} disabled={loading} loading={loading} onClick={childRegisterStart}>
                  {cParentOpen ? t('auth.child.register.sendCode') : t('auth.child.register.startStandalone')}
                </Button>
              </>
            )}

            {role === 'child' && mode === 'register' && childRegSub === 'code' && (
              <CodeStep
                introBefore={t('auth.child.register.codeSentTo')}
                introBoldValue={cParentEmail}
                introAfter={t('auth.child.register.codeTail')}
                devCode={cDevCode}
                code={cCode} setCode={setCCode}
                onSubmit={childRegisterComplete}
                submitLabel={t('auth.child.register.activate')}
                onBack={() => { setChildRegSub('form'); clearStatus(); }}
                err={err} loading={loading}
              />
            )}

            {/* ─── Parent login ─── */}
            {role === 'parent' && mode === 'login' && (
              <>
                <BigLabel>{t('auth.parent.email.label')}</BigLabel>
                <input type="email" autoComplete="email" placeholder={t('auth.parent.email.placeholder')}
                       value={pEmail} onChange={e => setPEmail(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && parentLoginPassword()} style={BIG_INPUT} />
                <BigLabel topGap>{t('auth.parent.password.label')}</BigLabel>
                <input type="password" autoComplete="current-password" value={pPassword}
                       onChange={e => setPPassword(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && parentLoginPassword()} style={BIG_INPUT} />
                <ErrorBox err={err} />
                <Button block size="lg" style={BIG_CTA} disabled={loading} loading={loading} onClick={parentLoginPassword}>
                  {t('auth.parent.login.submit')}
                </Button>
                <button type="button"
                        onClick={() => { setResetEmail(pEmail); setForgotSub('email'); jump(3, undefined, 'forgot'); }}
                        style={{
                          marginTop: 12, width: '100%', background: 'transparent', border: 0,
                          color: 'var(--xpb-primary)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                        }}>
                  {t('auth.parent.login.forgot')}
                </button>
              </>
            )}

            {/* ─── Parent register ─── */}
            {role === 'parent' && mode === 'register' && parentRegSub === 'form' && (
              <>
                {/* PWA install nudge — shown right when a parent commits to
                    using the app long-term. Self-hides if already installed. */}
                <PwaInstallHint />
                <div className="muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
                  {t('auth.parent.register.intro')}
                </div>
                <BigLabel>{t('auth.parent.register.email.label')}</BigLabel>
                <input type="email" autoComplete="email" placeholder={t('auth.parent.email.placeholder')}
                       value={rEmail} onChange={e => setREmail(e.target.value)} style={BIG_INPUT} />
                <BigLabel topGap>{t('auth.parent.register.password.label')}</BigLabel>
                <input type="password" autoComplete="new-password" value={rPassword}
                       onChange={e => setRPassword(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && parentRegisterStart()} style={BIG_INPUT} />
                {/* Optional invite code — pre-filled from ?invite=. Both
                    inviter and invitee get rewarded after register-complete. */}
                <BigLabel topGap>{t('auth.parent.register.invite.label')}</BigLabel>
                <input type="text" autoCapitalize="off" autoCorrect="off" spellCheck={false}
                       placeholder={t('auth.parent.register.invite.placeholder')}
                       value={rInviteCode}
                       onChange={e => setRInviteCode(e.target.value.replace(/\s/g, '').toLowerCase())}
                       maxLength={12}
                       style={{ ...BIG_INPUT, letterSpacing: 2, textTransform: 'lowercase' }}
                       onKeyDown={e => e.key === 'Enter' && parentRegisterStart()} />
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {t('auth.parent.register.invite.hint')}
                </div>
                <ErrorBox err={err} />
                <Button block size="lg" style={BIG_CTA} disabled={loading} loading={loading} onClick={parentRegisterStart}>
                  {t('auth.parent.register.sendCode')}
                </Button>
              </>
            )}

            {role === 'parent' && mode === 'register' && parentRegSub === 'code' && (
              <>
                {/* While they wait for the email, nudge "install the app". */}
                <PwaInstallHint />
                <CodeStep
                  introBefore={t('auth.parent.register.codeSentTo')}
                  introBoldValue={rEmail}
                  introAfter={t('auth.parent.register.codeTail')}
                  devCode={rDevCode}
                  code={rCode} setCode={setRCode}
                  onSubmit={parentRegisterComplete}
                  submitLabel={t('auth.parent.register.activate')}
                  onBack={() => { setParentRegSub('form'); clearStatus(); }}
                  err={err} loading={loading}
                />
              </>
            )}

            {/* ─── Parent password reset ─── */}
            {role === 'parent' && mode === 'forgot' && forgotSub === 'email' && (
              <>
                <div className="muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
                  {t('auth.parent.reset.intro')}
                </div>
                <BigLabel>{t('auth.parent.email.label')}</BigLabel>
                <input type="email" autoComplete="email" placeholder={t('auth.parent.email.placeholder')}
                       value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && parentResetStart()} style={BIG_INPUT} />
                <ErrorBox err={err} />
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <Button variant="secondary" onClick={() => jump(3, undefined, 'login')} disabled={loading} style={{ minHeight: 56 }}>
                    {t('common.back')}
                  </Button>
                  <Button style={{ flex: 1, minHeight: 56, fontSize: 16 }} disabled={loading} loading={loading} onClick={parentResetStart}>
                    {t('auth.parent.reset.send')}
                  </Button>
                </div>
              </>
            )}

            {role === 'parent' && mode === 'forgot' && forgotSub === 'set' && (
              <>
                <div className="muted" style={{ marginBottom: 12 }}>
                  {t('auth.parent.reset.sentTo')} <b>{resetEmail}</b>
                </div>
                {resetDevCode && (
                  <Card variant="success" style={{ fontSize: 13, marginBottom: 12 }}>
                    <b>{t('auth.parent.code.devHeader')}</b> {t('auth.parent.code.devLabel')} <code style={{ fontSize: 16 }}>{resetDevCode}</code>
                  </Card>
                )}
                <BigLabel>{t('auth.parent.code.label')}</BigLabel>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                       placeholder={t('auth.parent.code.placeholder')}
                       value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, ''))}
                       style={{ ...BIG_INPUT, textAlign: 'center', fontSize: 28, letterSpacing: 10, height: 60 }} />
                <BigLabel topGap>{t('auth.parent.reset.newPassword')}</BigLabel>
                <input type="password" autoComplete="new-password" value={resetPassword}
                       onChange={e => setResetPassword(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && parentResetComplete()} style={BIG_INPUT} />
                <ErrorBox err={err} />
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <Button variant="secondary" onClick={() => { setForgotSub('email'); clearStatus(); }} disabled={loading} style={{ minHeight: 56 }}>
                    {t('common.back')}
                  </Button>
                  <Button style={{ flex: 1, minHeight: 56, fontSize: 16 }} onClick={parentResetComplete} disabled={loading} loading={loading}>
                    {t('auth.parent.reset.submit')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Device hints — only after the user picks a role so we're not
          showing iPad warnings to a parent on a phone. */}
      {step >= 2 && role === 'child' && <DeviceHint variant={isIpad() ? 'ok' : 'warn'} />}
      {step >= 2 && role === 'parent' && <DeviceHint variant="parent" />}
    </div>
  );
}

// ─── Layout primitives ──────────────────────────────────────────────────

function BrandHeader() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      <div style={{ fontSize: 64, animation: 'float 2.4s ease-in-out infinite' }}>🐹</div>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginTop: 8, marginBottom: 2, color: 'var(--xpb-text-primary)' }}>
        {t('auth.brand.title')}
      </h1>
      <div className="muted" style={{ fontSize: 14 }}>{t('auth.brand.tagline')}</div>
    </div>
  );
}

function StepDots({ step, total }: { step: number; total: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {Array.from({ length: total }).map((_, i) => {
        const n = i + 1;
        const isActive = n === step;
        const isDone = n < step;
        return (
          <span key={n} aria-hidden style={{
            width: isActive ? 22 : 8, height: 8, borderRadius: 999,
            background: isActive || isDone ? 'var(--xpb-primary)' : 'rgba(0,0,0,0.12)',
            transition: 'width 0.2s ease, background 0.2s ease',
          }} />
        );
      })}
      <span className="muted" style={{ fontSize: 12, marginLeft: 6, fontWeight: 600 }}>
        {t('auth.wizard.step.label', { n: step, total })}
      </span>
    </div>
  );
}

function BigHeading({ children }: { children: ReactNode }) {
  return (
    <h2 style={{
      fontSize: 24, fontWeight: 700, marginTop: 0, marginBottom: 4,
      color: 'var(--xpb-text-primary)', textAlign: 'center',
    }}>{children}</h2>
  );
}

function Sub({ children }: { children: ReactNode }) {
  return (
    <div className="muted" style={{ textAlign: 'center', marginBottom: 18, fontSize: 14 }}>
      {children}
    </div>
  );
}

// Big tap-target choice button used in step 1 & step 2.
// 84px min-height = a healthy tap target on iPad and phone.
function BigChoice({
  emoji, title, subtitle, onClick,
}: { emoji: string; title: string; subtitle: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{
      minHeight: 84, padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left',
      background: 'var(--xpb-surface)', border: '1.5px solid var(--xpb-border-hover)',
      borderRadius: 16, cursor: 'pointer',
      transition: 'transform 0.1s ease, border-color 0.15s ease, background 0.15s ease',
    }}
      onPointerDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.985)';
        e.currentTarget.style.background = 'var(--xpb-primary-bg)';
        e.currentTarget.style.borderColor = 'var(--xpb-primary)';
      }}
      onPointerUp={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.background = 'var(--xpb-surface)';
        e.currentTarget.style.borderColor = 'var(--xpb-border-hover)';
      }}
      onPointerLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.background = 'var(--xpb-surface)';
        e.currentTarget.style.borderColor = 'var(--xpb-border-hover)';
      }}>
      <span style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>{emoji}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--xpb-text-primary)', lineHeight: 1.2 }}>
          {title}
        </span>
        <span style={{ fontSize: 13, color: 'var(--xpb-text-secondary)', lineHeight: 1.3 }}>
          {subtitle}
        </span>
      </span>
      <span style={{ fontSize: 24, color: 'var(--xpb-text-muted)', lineHeight: 1 }} aria-hidden>›</span>
    </button>
  );
}

// Shared "enter the 6-digit code" sub-step. Used by both child- and
// parent-register flows — they share the same UI and only differ on
// labels (passed in via props).
function CodeStep({
  introBefore, introBoldValue, introAfter, devCode,
  code, setCode, onSubmit, submitLabel, onBack, err, loading,
}: {
  introBefore: string; introBoldValue: string; introAfter: string;
  devCode: string | null;
  code: string; setCode: (v: string) => void;
  onSubmit: () => void; submitLabel: string;
  onBack: () => void;
  err: string | null; loading: boolean;
}) {
  return (
    <>
      <div className="muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
        {introBefore} <b>{introBoldValue}</b>{introAfter}
      </div>
      {devCode && (
        <Card variant="success" style={{ fontSize: 13, marginBottom: 12 }}>
          <b>{t('auth.parent.code.devHeader')}</b> {t('auth.parent.code.devLabel')} <code style={{ fontSize: 16 }}>{devCode}</code>
        </Card>
      )}
      <BigLabel>{t('auth.parent.code.label')}</BigLabel>
      <input inputMode="numeric" autoComplete="one-time-code" maxLength={6}
             placeholder={t('auth.parent.code.placeholder')}
             value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
             onKeyDown={e => e.key === 'Enter' && onSubmit()}
             style={{ ...BIG_INPUT, textAlign: 'center', fontSize: 28, letterSpacing: 10, height: 60 }} />
      <ErrorBox err={err} />
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onBack} disabled={loading} style={{ minHeight: 56 }}>
          {t('common.back')}
        </Button>
        <Button style={{ flex: 1, minHeight: 56, fontSize: 16 }} disabled={loading} loading={loading} onClick={onSubmit}>
          {submitLabel}
        </Button>
      </div>
    </>
  );
}

function BigLabel({ children, topGap = false }: { children: ReactNode; topGap?: boolean }) {
  return (
    <label style={{
      display: 'block', fontWeight: 700, fontSize: 15,
      marginBottom: 6, marginTop: topGap ? 14 : 0,
      color: 'var(--xpb-text-primary)',
    }}>{children}</label>
  );
}

function ErrorBox({ err }: { err: string | null }) {
  if (!err) return null;
  return <Card variant="danger" style={{ marginTop: 12, fontSize: 13 }}>{err}</Card>;
}

function DeviceHint({ variant }: { variant: 'ok' | 'warn' | 'parent' }) {
  const base: CSSProperties = { marginTop: 14, padding: '10px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.55 };
  if (variant === 'ok') {
    return <div style={{ ...base, background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.35)' }}>
      {t('auth.device.ipad.ok')}
    </div>;
  }
  if (variant === 'warn') {
    return <div style={{ ...base, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)' }}>
      {t('auth.device.ipad.warn')}
    </div>;
  }
  return <div style={{ ...base, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.35)' }}>
    {t('auth.parent.deviceHint')}<b>{t('auth.parent.deviceHintBold')}</b>{t('auth.parent.deviceHintTail')}
  </div>;
}

// ─── Reusable style snippets ────────────────────────────────────────────
const BIG_INPUT: CSSProperties = { height: 52, fontSize: 16, padding: '0 14px', width: '100%', boxSizing: 'border-box' };
const BIG_CTA: CSSProperties = { marginTop: 16, minHeight: 60, fontSize: 16 };
const BACK_BTN: CSSProperties = {
  background: 'transparent', border: 0,
  color: 'var(--xpb-primary-hover)', fontWeight: 700, fontSize: 15,
  padding: '6px 4px', cursor: 'pointer',
};

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || t('auth.error.generic');
}
